import React, { useEffect, useState, useContext } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Users, Puzzle, Star } from "lucide-react";
import {
  collection,
  getDocs,
  getCountFromServer,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { UserContext } from "../context/UserContext";
import AuthModal from "../components/modal/AuthModal";

const CommunityStatsSection = () => {
  const [stats, setStats] = useState({
    developersCount: "--",
    issuesSolved: "--",
    avgRating: "--",
  });
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { user } = useContext(UserContext);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // 🔹 Count users
        const usersCountSnap = await getCountFromServer(
          query(collection(db, "users"))
        );
        const devCount = usersCountSnap.data().count;

        // 🔹 Count issues
        const issuesCountSnap = await getCountFromServer(
          query(collection(db, "codeIssues"))
        );
        const issuesCount = issuesCountSnap.data().count;

        // 🔹 Fetch all ratings and calculate average
        const ratingsSnap = await getDocs(
          query(collection(db, "ratings"), orderBy("createdAt", "desc"))
        );
        const ratings = ratingsSnap.docs.map((d) => d.data().rating || 0);
        const avg =
          ratings.length > 0
            ? (ratings.reduce((sum, r) => sum + r, 0) / ratings.length).toFixed(
                1
              )
            : "--";

        // 🔹 Update State
        setStats({
          developersCount: devCount.toLocaleString(),
          issuesSolved: issuesCount.toLocaleString(),
          avgRating: avg,
        });
      } catch (err) {
        console.error("Error fetching community stats:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statArray = [
    {
      icon: <Users size={36} />,
      value: stats.developersCount,
      label: "Developers Joined",
    },
    {
      icon: <Puzzle size={36} />,
      value: stats.issuesSolved,
      label: "Issues Posted",
    },
    {
      icon: <Star size={36} />,
      value: stats.avgRating,
      label: "Average Rating",
    },
  ];

  const handleSignupClick = () => {
    if (!user) {
      setShowAuthModal(true);
    } else {
      // Already logged in, no modal needed
    }
  };

  return (
    <SectionWrapper id="community">
      {/* 🔹 Auth Modal */}
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      )}

      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Join a <Highlight>Thriving Developer Community</Highlight>
      </motion.h2>

      <Subtitle>
        Thousands of developers solving issues, earning rewards, and ranking
        globally.
      </Subtitle>

      {loading ? (
        <Loading>Loading community stats...</Loading>
      ) : (
        <>
          <StatsGrid>
            {statArray.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.2 }}
              >
                <StatCard>
                  <IconWrapper>{stat.icon}</IconWrapper>
                  <StatValue>{stat.value}</StatValue>
                  <StatLabel>{stat.label}</StatLabel>
                </StatCard>
              </motion.div>
            ))}
          </StatsGrid>

          <ButtonsGroup>
            <PrimaryButton
              as="a"
              href="https://chat.whatsapp.com/DJ99t0ZZuuUHELAht7lNh9"
              target="_blank"
              rel="noopener noreferrer"
            >
              Join WhatsApp Community
            </PrimaryButton>

            <SecondaryButton
              as="a"
              href="  https://discord.gg/N4vxqv54"
              target="_blank"
              rel="noopener noreferrer"
            >
              Join Discord Server
            </SecondaryButton>
            {!user && (
              <TertiaryButton onClick={handleSignupClick}>
                Sign Up Now
              </TertiaryButton>
            )}
          </ButtonsGroup>
        </>
      )}
    </SectionWrapper>
  );
};

export default CommunityStatsSection;

/* ---------------- Styled Components ---------------- */

const SectionWrapper = styled.section`
  width: 100%;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  padding: 120px 40px;
  text-align: center;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #d2cde9;
  margin-top: 10px;
  margin-bottom: 60px;
`;

const Loading = styled.p`
  color: #c59fff;
  font-size: 1.1rem;
  margin-top: 20px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 40px;
  justify-content: center;
  margin-bottom: 60px;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 40px 20px;
  backdrop-filter: blur(6px);
  transition: 0.3s;
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 0 25px rgba(155, 92, 255, 0.25);
  }
`;

const IconWrapper = styled.div`
  color: #c59fff;
  margin-bottom: 15px;
`;

const StatValue = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: #fff;
  margin: 0;
`;

const StatLabel = styled.p`
  color: #cfcfcf;
  font-size: 0.95rem;
  margin-top: 5px;
`;

const ButtonsGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
`;

const PrimaryButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  border: none;
  border-radius: 10px;
  padding: 14px 26px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  font-size: 1rem;
  transition: 0.3s;
  text-decoration: none;
  display: inline-block;
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 20px rgba(155, 92, 255, 0.4);
  }
`;

const SecondaryButton = styled(PrimaryButton)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.4);
  color: #fff;
  &:hover {
    background: rgba(155, 92, 255, 0.15);
  }
`;

const TertiaryButton = styled(PrimaryButton)`
  background: linear-gradient(90deg, #c59fff, #9b5cff);
`;
